import { useState } from 'react'
import { Shield, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore, selectRole, selectIsSuperAdmin } from '@/stores/authStore'

const DISMISSED_KEY = 'maktab.elevated-banner-dismissed'

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Reminder that the signed-in person has system-administrator access: one compact line. On
 * phones it can be dismissed for the session, so it does not take space on every page.
 */
export function SystemAdminAlert() {
  const role = useAuthStore(selectRole)
  const isSuperAdmin = useAuthStore(selectIsSuperAdmin)
  const [dismissed, setDismissed] = useState(readDismissed)

  // Show alert for SYSTEM_ADMIN role or super admin
  const shouldShow = role === 'SYSTEM_ADMIN' || isSuperAdmin

  if (!shouldShow) return null

  function dismiss() {
    setDismissed(true)
    try {
      window.sessionStorage.setItem(DISMISSED_KEY, '1')
    } catch {
      // Dismissal then only lasts until the next render of this page.
    }
  }

  return (
    <div
      className={cn(
        'border-amber-200 border-b bg-gradient-to-r from-amber-50 to-yellow-50 px-3 py-1.5 sm:px-4',
        dismissed && 'max-sm:hidden',
      )}
    >
      <div className='flex items-center justify-center gap-2 text-amber-800 text-xs sm:text-sm'>
        <Shield className='h-3.5 w-3.5 shrink-0 text-amber-600 sm:h-4 sm:w-4' aria-hidden />
        <span className='truncate'>
          <span className='font-medium'>Elevated permissions</span>
          <span className='hidden sm:inline'> · You have system administrator access</span>
        </span>
        <button
          type='button'
          onClick={dismiss}
          aria-label='Dismiss the elevated permissions notice'
          className='-mr-1 ml-auto inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-amber-700 hover:bg-amber-100 sm:hidden'
        >
          <X className='h-3.5 w-3.5' aria-hidden />
        </button>
      </div>
    </div>
  )
}
